import axios from 'axios';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import * as qs from 'qs';

export interface OAuth2Config {
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
  identityProvider: string;
  userPoolId: string;
}

export interface DecodedToken {
  sub: string;
  email: string;
  given_name?: string;
  family_name?: string;
  identities?: Array<{
    userId: string;
    providerName: string;
    providerType: string;
    issuer?: string;
    primary?: string;
  }>;
  'cognito:groups'?: string[];
  [key: string]: any;
}

export interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export class OAuth2Service {
  private config: OAuth2Config;

  constructor(config: OAuth2Config) {
    this.config = config;
  }

  /**
   * Generate the authorization URL for federated login
   */
  getAuthorizationUrl(): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      scope: 'email openid',
      redirect_uri: this.config.redirectUri,
      identity_provider: this.config.identityProvider
    });

    return `https://${this.config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Extract authorization code from URL parameters
   */
  getAuthorizationCodeFromUrl(): string | null {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('code');
  }

  /**
   * Exchange authorization code for tokens
   */
  async exchangeCodeForTokens(code: string): Promise<TokenResponse> {
    console.log('Token exchange initiated with code length:', code?.length || 0);
    console.log('Token exchange timestamp:', new Date().toISOString());
    
    // Log the configuration for debugging
    console.log('OAuth2Service Configuration:', {
      cognitoDomain: this.config.cognitoDomain,
      clientId: this.config.clientId,
      redirectUri: this.config.redirectUri,
      identityProvider: this.config.identityProvider,
      userPoolId: this.config.userPoolId
    });
    
    // Compare with environment variables
    console.log('Environment Variables:', {
      REACT_APP_COGNITO_DOMAIN: process.env.REACT_APP_COGNITO_DOMAIN,
      REACT_APP_COGNITO_CLIENT_ID: process.env.REACT_APP_COGNITO_CLIENT_ID,
      REACT_APP_REDIRECT_SIGN_IN: process.env.REACT_APP_REDIRECT_SIGN_IN
    });
    
    const data = qs.stringify({
      'grant_type': 'authorization_code',
      'client_id': this.config.clientId,
      'code': code,
      'redirect_uri': this.config.redirectUri
    });

    console.log('Token exchange request details:', {
      tokenUrl: `https://${this.config.cognitoDomain}/oauth2/token`,
      clientId: this.config.clientId,
      redirectUri: this.config.redirectUri,
      codeLength: code ? code.length : 0
    });

    const config = {
      method: 'post' as const,
      url: `https://${this.config.cognitoDomain}/oauth2/token`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      data: data,
      timeout: 10000 // 10 second timeout
    };

    try {
      const startTime = Date.now();
      const response = await axios(config);
      const endTime = Date.now();
      
      console.log('Token exchange successful in', endTime - startTime, 'ms');
      console.log('Token response contains:', Object.keys(response.data).join(', '));
      
      return response.data;
    } catch (error: any) {
      // Enhanced error logging
      console.error('Token exchange failed with details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorType: error.response?.data?.error,
        errorDescription: error.response?.data?.error_description,
        requestUrl: error.config?.url,
        timestamp: new Date().toISOString()
      });
      
      // Check for timeout
      if (error.code === 'ECONNABORTED') {
        console.error('Token exchange request timed out');
        throw new Error('Authentication request timed out. Please try again.');
      }
      
      // Check for specific error types
      if (error.response?.data?.error === 'invalid_grant') {
        console.error('Invalid grant error - authorization code may have been used already or expired');
        throw new Error('Authentication code expired or already used. Please try logging in again.');
      }
      
      throw new Error(`Token exchange failed: ${error.response?.data?.error_description || error.message}`);
    }
  }

  /**
   * Decode and validate the ID token
   */
  decodeIdToken(idToken: string): DecodedToken {
    try {
      const decodedToken = jwtDecode<DecodedToken>(idToken);
      
      // Add detailed logging of the token
      console.log('Decoded JWT Token:', JSON.stringify(decodedToken, null, 2));
      
      // Log specific claims that might be useful for admin determination
      console.log('JWT Subject:', decodedToken.sub);
      console.log('JWT Email:', decodedToken.email);
      console.log('JWT Groups:', decodedToken['cognito:groups']);
      console.log('JWT Identities:', decodedToken.identities);
      
      // Log all available claims for inspection
      console.log('All JWT Claims:', Object.keys(decodedToken));
      
      return decodedToken;
    } catch (error) {
      console.error('Error decoding ID token:', error);
      throw new Error('Invalid ID token format');
    }
  }

  /**
   * Create a Cognito user session from OAuth2 tokens
   */
  async createCognitoSession(tokens: TokenResponse): Promise<AmazonCognitoIdentity.CognitoUserSession> {
    try {
      // Decode the ID token to get user information
      const decodedToken = this.decodeIdToken(tokens.id_token);
      
      // Create Cognito token objects
      const accessToken = new AmazonCognitoIdentity.CognitoAccessToken({ 
        AccessToken: tokens.access_token 
      });
      
      const idToken = new AmazonCognitoIdentity.CognitoIdToken({ 
        IdToken: tokens.id_token 
      });
      
      const refreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({ 
        RefreshToken: tokens.refresh_token 
      });

      // Create session data
      const sessionData = {
        IdToken: idToken,
        AccessToken: accessToken,
        RefreshToken: refreshToken
      };

      // Create the user session
      const userSession = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

      // Store user ID in localStorage (following the pattern from requirements)
      if (decodedToken.identities && decodedToken.identities.length > 0) {
        localStorage.setItem('uId', decodedToken.identities[0].userId);
      } else {
        localStorage.setItem('uId', decodedToken.sub);
      }

      return userSession;
    } catch (error) {
      console.error('Error creating Cognito session:', error);
      throw new Error('Failed to create user session');
    }
  }

  /**
   * Create a Cognito user object from the decoded token
   */
  createCognitoUser(decodedToken: DecodedToken): AmazonCognitoIdentity.CognitoUser {
    // Determine the username - use the federated user ID if available, otherwise use sub
    const username = decodedToken.identities && decodedToken.identities.length > 0 
      ? decodedToken.identities[0].userId 
      : decodedToken.sub;

    // Create user pool data
    const userPoolData = {
      UserPoolId: this.config.userPoolId,
      ClientId: this.config.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(userPoolData);

    // Create user data
    const userData = {
      Username: username,
      Pool: userPool
    };

    return new AmazonCognitoIdentity.CognitoUser(userData);
  }

  /**
   * Complete OAuth2 flow - exchange code for tokens and create session
   */
  async completeOAuth2Flow(code: string): Promise<{
    user: AmazonCognitoIdentity.CognitoUser;
    session: AmazonCognitoIdentity.CognitoUserSession;
    decodedToken: DecodedToken;
  }> {
    try {
      // Exchange code for tokens
      const tokens = await this.exchangeCodeForTokens(code);
      
      // Decode the ID token
      const decodedToken = this.decodeIdToken(tokens.id_token);
      
      // Create Cognito session
      const session = await this.createCognitoSession(tokens);
      
      // Create Cognito user
      const user = this.createCognitoUser(decodedToken);
      
      // Set the session on the user
      user.setSignInUserSession(session);

      return { user, session, decodedToken };
    } catch (error) {
      console.error('Error completing OAuth2 flow:', error);
      throw error;
    }
  }

  /**
   * Validate a Cognito session
   */
  async validateSession(
    user: AmazonCognitoIdentity.CognitoUser, 
    session: AmazonCognitoIdentity.CognitoUserSession
  ): Promise<boolean> {
    return new Promise((resolve) => {
      user.getSession((err: any, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
        if (err || !session) {
          console.error('Session validation failed:', err);
          resolve(false);
        } else {
          resolve(session.isValid());
        }
      });
    });
  }

  /**
   * Redirect to federated login
   */
  redirectToFederatedLogin(): void {
    const authUrl = this.getAuthorizationUrl();
    console.log('Redirecting to federated login:', authUrl);
    window.location.href = authUrl;
  }

  /**
   * Clear stored authentication data
   */
  clearStoredData(): void {
    localStorage.removeItem('uId');
  }
}

export default OAuth2Service;
